import React from 'react'
import moment from 'moment'
// @src imports
import { Details, MoreInfo, SubDate } from 'src/styled'
import { Button } from 'src/chrome'
import { formatCost, sortedSubscriptionFeatures } from 'src/helpers'
import {
  NormalizedSubscriptionFeature,
  SubscriptionType,
} from 'src/user/fragments'
import { StripeSubscription } from 'src/legacy_graphql'
import { Feature } from 'src/dashboard/components'
import {
  ConfirmDialog,
  Flex,
  HStack,
  Text,
} from '@sendoutcards/quantum-design-ui'
import { useActions, useMutations, useState } from 'src/hooks'

interface Props {
  subscription: SubscriptionType | StripeSubscription
  shouldShowDates: boolean
  shouldShowPrice?: boolean
  onChangeSubscription?: () => void
  isChangingSubscription?: boolean
  handleLaunchSubscriptionChanger?: () => void
}

const Subscription: React.FC<Props> = props => {
  const {
    subscription: {
      id,
      name,
      price,
      startDate,
      currentPeriodEnd,
      isSetToCancel,
      isRenewable,
      features,
    },
    shouldShowDates,
    shouldShowPrice = true,
    onChangeSubscription,
    isChangingSubscription,
    handleLaunchSubscriptionChanger,
  } = props
  const actions = useActions()
  const mutations = useMutations()

  const [
    isShowingCancellationDialog,
    setIsShowingCancellationDialog,
  ] = useState(false)

  const isSystemPackage = name.includes('System')
  const isCardPackage = name.includes('Card Package')
  const is10for20 = name === '10 for $20 Heartfelt Cards'
  const isAffiliateSubscription = id === 'sub_1N4QvpASHJWgnbKEUP4cZ6Ur'

  const cancelSubscription = async () => {
    const {
      cancelStripeSubscription: { account },
    } = await mutations.cancelStripeSubscription({ id })
    actions.updatedAccount(account)
  }

  return (
    <>
      <Flex justifyContent="space-between">
        <Text type="caption" color="primaryHeading" weight="bold">
          {name}
          {name === 'Enterprise Grandfathered' ? (
            <MoreInfo
              href={
                'https://www.sendoutcards.com/resources/enterprise-subscription-details/'
              }
              target="_blank"
            >
              More Information
            </MoreInfo>
          ) : null}
        </Text>
        {shouldShowPrice && <Text type="caption">{formatCost(price)}</Text>}
      </Flex>
      {shouldShowDates && (
        <Details>
          {startDate && currentPeriodEnd && (
            <>
              <SubDate>
                <Text type="caption" color="primaryHeading" weight="bold">
                  Created
                </Text>
                <Text type="caption">
                  {moment(startDate).format('MM/DD/YYYY')}
                </Text>
              </SubDate>
              <SubDate style={isSetToCancel ? { color: 'red' } : {}}>
                <Text
                  type="caption"
                  color={isSetToCancel ? 'danger' : 'primaryHeading'}
                  weight="bold"
                >
                  {is10for20
                    ? ''
                    : isSystemPackage || isCardPackage
                    ? 'Expires'
                    : 'Renewal Date'}
                </Text>
                <Flex width="100%" justifyContent="space-between">
                  <Text
                    type="caption"
                    color={isSetToCancel ? 'danger' : 'primaryHeading'}
                    alignment="right"
                  >
                    {is10for20
                      ? 'Ends On '
                      : isSetToCancel && !isSystemPackage && !isCardPackage
                      ? 'Will cancel on '
                      : ''}
                  </Text>
                  <Text
                    type="caption"
                    color={isSetToCancel ? 'danger' : 'primaryHeading'}
                    alignment="right"
                  >
                    {moment(currentPeriodEnd).format('MM/DD/YYYY')}
                  </Text>
                </Flex>
              </SubDate>
              {!isCardPackage && (
                <HStack gap="x1" justify="flex-end" alignItems="end">
                  {!isSystemPackage &&
                    !!onChangeSubscription &&
                    isSetToCancel &&
                    isRenewable && (
                      <Button
                        id={'renew_subscription_btn'}
                        style={{ marginLeft: 'auto' }}
                        title={'Renew'}
                        onClick={onChangeSubscription}
                        disabled={isChangingSubscription}
                      />
                    )}
                  {handleLaunchSubscriptionChanger &&
                    !isAffiliateSubscription && (
                      <Button
                        title={'Change Subscription'}
                        onClick={handleLaunchSubscriptionChanger}
                      />
                    )}
                  <Button
                    id={'cancel_subscription_btn'}
                    style={{ marginLeft: 'auto' }}
                    title={'Cancel'}
                    onClick={() => setIsShowingCancellationDialog(true)}
                    disabled={isChangingSubscription}
                  />
                </HStack>
              )}
            </>
          )}
        </Details>
      )}
      {features &&
        sortedSubscriptionFeatures(features)
          .filter(f => f !== null)
          .map(key => (
            <Feature
              key={key}
              feature={features[key] as NormalizedSubscriptionFeature}
            />
          ))}
      <ConfirmDialog
        isOpen={isShowingCancellationDialog}
        title="Cancel Subscription"
        description="Are you sure you want to cancel this subscription?"
        decline={{
          title: 'Keep Subscription',
          onClick: () => setIsShowingCancellationDialog(false),
        }}
        accept={{
          title: 'Cancel Subscription',
          onClick: async () => {
            await cancelSubscription()
            setIsShowingCancellationDialog(false)
          },
        }}
        zIndex={1}
      />
    </>
  )
}

export default Subscription
